import { v4 as uuidv4 } from 'uuid';
import type { MessageDto } from '@/types';
import { MessageType } from '@/types';
import { getRichTextPlainContent } from '@/lib/richText';

// ============================================
// Avatar & Color Utilities
// ============================================

export const avatarDefault = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';

export function getAvatarColor(name: string): string {
    if (!name) {
        return '#CCCCCC';
    }

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = hash % 360;
    const saturation = 70;
    const lightness = 40;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export function getInitials(name: string): string {
    if (!name) return '??';
    const cleanName = name.trim();
    const parts = cleanName.split(/\s+/);

    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    if (cleanName.length >= 2) {
        return cleanName.substring(0, 2).toUpperCase();
    }
    return (cleanName + cleanName).substring(0, 2).toUpperCase();
}

// ============================================
// ID Generators
// ============================================

export function createUniqueId(): string {
    return uuidv4();
}

// ============================================
// File Size Formatter
// ============================================

export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// ============================================
// Duration Formatter
// ============================================

export function formatDuration(seconds: number): string {
    if (!seconds || seconds < 0) return '0:00';

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================
// Time Translations Type
// ============================================

export interface TimeTranslations {
    yesterday: string;
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
    weeksAgo: string;
    monthsAgo: string;
    yearsAgo: string;
}

export interface MessagePreviewLabels {
    newMessage: string;
    customMessage: string;
    sticker: string;
    image: string;
    video: string;
    file: string;
    voice: string;
    location: string;
    replyMessage: string;
    thisMessageWasDeleted: string;
}

// Language code to locale mapping for date formatting
const languageToLocale: Record<string, string> = {
    en: 'en-US',
    es: 'es-ES',
    ja: 'ja-JP',
    ko: 'ko-KR',
    zh: 'zh-CN',
};

export function getLocale(lang?: string): string {
    return lang ? (languageToLocale[lang] || 'en-US') : 'en-US';
}

// ============================================
// Timestamp Formatter
// ============================================

export function formatTimestamp(timestamp: number, t?: { yesterday: string }, lang?: string): string {
    if (!timestamp) return '';

    const locale = lang ? (languageToLocale[lang] || 'en-US') : 'en-US';
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (messageDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } else if (messageDate.getTime() === yesterday.getTime()) {
        const yesterdayText = t?.yesterday || 'Yesterday';
        return yesterdayText + ' ' + date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } else if (now.getFullYear() === date.getFullYear()) {
        return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) + ' ' +
            date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    } else {
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
    }
}

// ============================================
// Relative Time Formatter
// ============================================

export function formatRelativeTime(timestamp: number, t?: TimeTranslations): string {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) {
        return t?.justNow || 'Just now';
    } else if (minutes < 60) {
        return `${minutes}${t?.minutesAgo || 'm ago'}`;
    } else if (hours < 24) {
        return `${hours}${t?.hoursAgo || 'h ago'}`;
    } else if (days < 7) {
        return `${days}${t?.daysAgo || 'd ago'}`;
    } else if (weeks < 4) {
        return `${weeks}${t?.weeksAgo || 'w ago'}`;
    } else if (months < 12) {
        return `${months}${t?.monthsAgo || 'mo ago'}`;
    } else {
        return `${years}${t?.yearsAgo || 'y ago'}`;
    }
}

// ============================================
// Room Display Name
// ============================================

export function getRoomDisplayName(groupName: string, customerNo: string | undefined, maxLength?: number): string {
    if (!groupName && !customerNo) return '';
    if (!groupName) return customerNo || '';
    if (!customerNo || groupName === customerNo) return groupName;
    // If groupName already contains the customerNo in parentheses, return as-is
    if (groupName.includes(`(${customerNo})`)) return groupName;
    // Build full display: "CustomerName (CustomerNo)"
    const suffix = ` (${customerNo})`;
    const full = `${groupName}${suffix}`;
    // If no max length or it fits, return full string
    if (!maxLength || full.length <= maxLength) return full;
    // Truncate customer name to fit within maxLength, adding "..." when truncated
    const ellipsis = '...';
    const availableForName = maxLength - suffix.length - ellipsis.length;
    if (availableForName <= 0) return customerNo;
    const truncatedName = groupName.substring(0, availableForName).trimEnd();
    return `${truncatedName}${ellipsis}${suffix}`;
}

// ============================================
// Message Text Processing
// ============================================

export function processMessageText(text: string): string {
    if (!text) return '';
    // Convert newlines to <br> for display
    let result = text.replace(/\n/g, '<br>');
    // Highlight @mentions - allow dots, underscores, hyphens, and @ for emails
    result = result.replace(/@([a-zA-Z0-9._@+-]+)/g, '<span class="mention">@$1</span>');
    return result;
}

export function extractStickerUrls(text?: string): string[] {
    if (!text) return [];

    const trimmedText = text.trim();
    const stickerMatch = trimmedText.match(/^\[STICKERS:(.+)\]$/);
    if (stickerMatch) {
        return stickerMatch[1].split('|').map(url => url.trim()).filter(url => url);
    }

    if (trimmedText.match(/^https?:\/\/(media\.tenor\.com\/|.*\.(gif|GIF|png|PNG|webp|WEBP)).*$/)) {
        return [trimmedText];
    }

    return [];
}

function cleanPreviewLabel(value?: unknown, fallback: string = ''): string {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!trimmed || trimmed === '[NEWMSG]') return fallback;
    return trimmed;
}

export function getMessagePreviewText(message: MessageDto | null | undefined, labels?: Partial<MessagePreviewLabels>): string {
    if (!message) return '';

    const resolvedLabels: MessagePreviewLabels = {
        newMessage: 'New message',
        customMessage: 'Custom message',
        sticker: 'Sticker',
        image: 'Image',
        video: 'Video',
        file: 'File',
        voice: 'Voice',
        location: 'Location',
        replyMessage: 'Reply',
        thisMessageWasDeleted: 'This message was deleted',
        ...labels,
    };

    switch (message.contentType) {
        case MessageType.TextMessage:
        case MessageType.AtTextMessage:
        case MessageType.AdvancedTextMessage: {
            const text = (
                message.contentType === MessageType.AtTextMessage
                    ? message.atTextElem?.text
                    : message.textElem?.content
            )?.trim() || '';
            if (!text) return resolvedLabels.newMessage;
            if (extractStickerUrls(text).length > 0) return resolvedLabels.sticker;
            return getRichTextPlainContent(text);
        }
        case MessageType.PictureMessage:
            return resolvedLabels.image;
        case MessageType.VideoMessage:
            return resolvedLabels.video;
        case MessageType.FileMessage:
            return resolvedLabels.file;
        case MessageType.VoiceMessage:
            return resolvedLabels.voice;
        case MessageType.LocationMessage:
            return resolvedLabels.location;
        case MessageType.QuoteMessage:
            return message.quoteElem?.text?.trim() || resolvedLabels.replyMessage;
        case MessageType.RevokeMessage:
        case MessageType.SDK_RevokeMessage:
            return resolvedLabels.thisMessageWasDeleted;
        case MessageType.CustomMessage: {
            if (message.customElem?.data) {
                try {
                    const customData = JSON.parse(message.customElem.data);
                    if (customData.action === 'read') {
                        return '';
                    }
                    if (customData.type === 'reaction') {
                        const emoji = typeof customData.emoji === 'string' ? customData.emoji.trim() : '';
                        return emoji ? `Reacted ${emoji}` : 'Reacted to a message';
                    }
                    const customLabel = cleanPreviewLabel(customData.description || customData.title);
                    if (customLabel) return customLabel;
                } catch {
                    // Fall back to customElem description below.
                }
            }

            const description = cleanPreviewLabel(message.customElem?.description);
            return description || resolvedLabels.customMessage;
        }
        default:
            return resolvedLabels.newMessage;
    }
}

// ============================================
// Emoji List
// ============================================

export const emojiList = [
    '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
    '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘',
    '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝',
    '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐',
    '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌',
    '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢',
    '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠',
    '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️',
    '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨',
    '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞',
    '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬',
    '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙',
    '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️',
    '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '💪',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
    '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣',
];

// ============================================
// Reaction Emojis
// ============================================

export const reactionEmojis = [
    { emoji: '❤️', name: 'Love' },
    { emoji: '👍', name: 'Like' },
    { emoji: '😊', name: 'Happy' },
    { emoji: '😢', name: 'Sad' },
    { emoji: '😮', name: 'Surprised' },
    { emoji: '😡', name: 'Angry' }
];

// ============================================
// File Type Utilities
// ============================================

export function getFileExtension(mimeType: string): string {
    const mimeToExt: { [key: string]: string } = {
        'audio/webm': 'webm',
        'audio/webm;codecs=opus': 'webm',
        'audio/ogg;codecs=opus': 'ogg',
        'audio/ogg': 'ogg',
        'audio/mp4': 'm4a',
        'audio/mpeg': 'mp3'
    };
    return mimeToExt[mimeType] || 'webm';
}

export function getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
        pdf: '📄',
        doc: '📝',
        docx: '📝',
        xls: '📊',
        xlsx: '📊',
        ppt: '📽️',
        pptx: '📽️',
        txt: '📄',
        zip: '📦',
        rar: '📦',
        '7z': '📦',
    };
    return iconMap[ext] || '📎';
}

// ============================================
// Image Processing Utilities
// ============================================

export function calculateImageDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
): { width: number; height: number } {
    let width = originalWidth;
    let height = originalHeight;

    if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
    }

    if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
}

export async function createImagePreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file);
    });
}

export async function getVideoMetadata(file: File): Promise<{ duration: number; thumbnailFile: File; thumbnailDataUrl: string }> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
            return reject(new Error('Canvas 2D context is not available.'));
        }

        let duration = 0;

        video.addEventListener('loadedmetadata', () => {
            duration = video.duration;
            video.currentTime = 1;
        });

        video.addEventListener('seeked', () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            const thumbnailDataUrl = canvas.toDataURL('image/jpeg');

            canvas.toBlob((blob) => {
                if (!blob) {
                    return reject(new Error('Canvas to Blob conversion failed.'));
                }
                const thumbnailFile = new File([blob], `thumbnail_${file.name}.jpg`, { type: 'image/jpeg' });

                URL.revokeObjectURL(video.src);

                resolve({ duration, thumbnailFile, thumbnailDataUrl });
            }, 'image/jpeg');
        });

        video.addEventListener('error', (event) => {
            reject(new Error(`Error loading video: ${event.type}`));
            URL.revokeObjectURL(video.src);
        });

        video.src = URL.createObjectURL(file);
        video.load();
    });
}

// ============================================
// Toast Notification Helper
// ============================================

export function showToast(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info'): void {
    // Simple console-based toast for now
    // Can be replaced with a proper toast library
    console.log(`[${type.toUpperCase()}] ${message}`);

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    let toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = 'toast-container';
        toastContainer.setAttribute('aria-live', 'polite');
        toastContainer.setAttribute('aria-atomic', 'true');
        Object.assign(toastContainer.style, {
            position: 'fixed',
            top: '16px',
            right: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: 'min(360px, calc(100vw - 32px))',
            zIndex: '2147483647',
            pointerEvents: 'none',
        });
        document.body.appendChild(toastContainer);
    }

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;
    toast.textContent = message;
    toast.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');

    const backgroundByType = {
        success: '#0f8a5f',
        error: '#c84b31',
        info: '#1f4e79',
        warning: '#b7791f',
    } as const;

    Object.assign(toast.style, {
        background: backgroundByType[type],
        color: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 12px 32px rgba(15, 23, 42, 0.22)',
        padding: '12px 14px',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '1.4',
        opacity: '0',
        transform: 'translateY(-8px)',
        transition: 'opacity 180ms ease, transform 180ms ease',
        pointerEvents: 'auto',
        wordBreak: 'break-word',
    });

    toastContainer.appendChild(toast);

    while (toastContainer.childElementCount > 4) {
        toastContainer.firstElementChild?.remove();
    }

    requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
    });

    window.setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(-8px)';
        window.setTimeout(() => toast.remove(), 180);
    }, 4000);
}

// ============================================
// Local Storage Helpers
// ============================================

export function getFromStorage<T>(key: string): T | null {
    if (typeof window === 'undefined') return null;
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch {
        return null;
    }
}

export function setToStorage<T>(key: string, value: T): void {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.error('Failed to save to localStorage:', e);
    }
}

export function removeFromStorage(key: string): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
}

// ============================================
// Draft Management
// ============================================

export function saveDraft(conversationID: string, messageText: string): void {
    const draft = {
        timestamp: Date.now(),
        messageText: messageText || '',
    };

    if (draft.messageText.trim()) {
        setToStorage(`draft_${conversationID}`, draft);
    } else {
        removeFromStorage(`draft_${conversationID}`);
    }
}

export function loadDraft(conversationID: string): string {
    const draft = getFromStorage<{ timestamp: number; messageText: string }>(`draft_${conversationID}`);
    return draft?.messageText || '';
}

export function clearDraft(conversationID: string): void {
    removeFromStorage(`draft_${conversationID}`);
}

// ============================================
// Debounce Helper
// ============================================

export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;

    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}
